import * as React from 'react'
import { ariaProps, dataProps, onProps } from '@ds/react-utils'
import { css, CSSObject } from '@emotion/react'
import { useTranslate } from '@ds/comp-private'
import { useThemeStyles } from '../../../theming'
import type {
  AriaAttributes,
  ButtonForwardRef,
  EventListenerProps,
} from '../../../types'
import { ProgressCircle } from '../../../components/ProgressCircle'
import styles from './styles'

export type BaseButtonKind = (typeof baseButtonKinds)[number]
export type BaseButtonSizes = (typeof baseButtonSizes)[number]
export type BaseButtonType = (typeof baseButtonTypes)[number]

export const baseButtonSizes = ['small', 'medium'] as const
export const baseButtonKinds = [
  'brand',
  'primary',
  'secondary',
  'tertiary',
  'danger',
] as const
export const baseButtonTypes = ['button', 'submit', 'reset'] as const

export interface BaseButtonProps
  extends EventListenerProps<HTMLButtonElement>,
    AriaAttributes {
  /** The text to present to assistive devices in order to identify the button. */
  accessibilityText: string
  /** Adds a visual activated style state. For use only as the trigger for Callout when open. Pressed buttons should use the [ToggleButton](https://github.docusignhq.com/pages/front-end-systems/ds-ui/?path=/story/components-toolbar-togglebutton--configure) component for a11y. */
  activated?: boolean
  /** The content to display in the button. */
  children?: React.ReactNode
  /** Accepts custom data attributes. */
  'data-.*'?: string
  'data-qa'?: string
  /** Applies the 'disabled' attribute and disabled styles. */
  disabled?: boolean
  /** A React ref to assign to the HTML node representing the button element. */
  forwardedRef?: ButtonForwardRef
  /** The kind of the button. */
  kind?: BaseButtonKind
  /** Displays a loading indicator in the button. */
  loading?: boolean
  /** Accepts attributes matching the pattern on[A-Z].* in order to register event handlers. */
  'on[A-Z].*'?: React.EventHandler<React.SyntheticEvent<HTMLButtonElement>>
  /** The size of the button */
  size?: BaseButtonSizes
  /** @internal ds-ui custom style overrides for button components that have unique styles */
  styleOverrides?: CSSObject
  /** The title global attribute contains text representing advisory information related to the element it belongs to. */
  title?: string
  /**
   * The HTML 'type' attribute to apply to the rendered button element.
   */
  type?: BaseButtonType
}

export function BaseButton({
  activated = false,
  accessibilityText,
  children,
  'data-qa': dataQa,
  disabled = false,
  forwardedRef,
  kind,
  loading,
  size = 'medium',
  styleOverrides,
  title,
  type = 'button',
  ...restProps
}: BaseButtonProps) {
  const translate = useTranslate()

  /**
   * The BaseButton component is the source of truth for shared button styles.
   */
  const sx = useThemeStyles(styles, {
    kind,
    disabled,
    size,
  })

  const buttonStyles = css([
    sx.button.default,
    styleOverrides && styleOverrides,
    activated && sx.button.activated,
  ])

  const loadingNode = loading && (
    <ProgressCircle
      aria-hidden="true"
      dark={kind !== 'tertiary' && kind !== 'secondary'}
      data-qa={dataQa && `${dataQa}-loading`}
      hideText
      kind="subtle"
      size="small"
      text={translate('loading')}
    />
  )

  const ariaLabel = loading
    ? `${accessibilityText}, ${translate('loading')}`
    : accessibilityText

  return (
    <button
      {...ariaProps(restProps)}
      {...dataProps(restProps)}
      {...onProps(restProps)}
      aria-label={ariaLabel}
      aria-live={loading ? 'polite' : undefined}
      css={buttonStyles}
      data-qa={dataQa}
      disabled={disabled}
      ref={forwardedRef}
      title={title}
      // eslint-disable-next-line react/button-has-type
      type={type}
    >
      {loadingNode}
      {children}
    </button>
  )
}

BaseButton.displayName = 'BaseButton'
